import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import * as ratingApi from '../../api/ratingApi';

// Initial state
const initialState = {
  ratings: [],
  currentRating: null,
  pagination: {
    currentPage: 1,
    totalPages: 1,
    totalItems: 0,
    itemsPerPage: 10
  },
  loading: false,
  error: null,
  submitStatus: 'idle', // 'idle' | 'loading' | 'succeeded' | 'failed'
  submitError: null
};

// Async thunks
export const submitNewRating = createAsyncThunk(
  'rating/submitNewRating',
  async (ratingData, { rejectWithValue }) => {
    try {
      const response = await ratingApi.submitRating(ratingData);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const fetchRatings = createAsyncThunk(
  'rating/fetchRatings',
  async ({ page = 1, limit = 10, filters = {} } = {}, { rejectWithValue }) => {
    try {
      const params = { page, limit, ...filters };
      const response = await ratingApi.getRatings(params);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const fetchRatingById = createAsyncThunk(
  'rating/fetchRatingById',
  async (id, { rejectWithValue }) => {
    try {
      const response = await ratingApi.getRatingById(id);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

// Slice
const ratingSlice = createSlice({
  name: 'rating',
  initialState,
  reducers: {
    clearRatingError: (state) => {
      state.error = null;
      state.submitError = null;
    },
    resetSubmitStatus: (state) => {
      state.submitStatus = 'idle';
      state.submitError = null;
    },
    clearCurrentRating: (state) => {
      state.currentRating = null;
    }
  },
  extraReducers: (builder) => {
    builder
      // Submit rating
      .addCase(submitNewRating.pending, (state) => {
        state.submitStatus = 'loading';
        state.submitError = null;
      })
      .addCase(submitNewRating.fulfilled, (state, action) => {
        state.submitStatus = 'succeeded';
        if (action.payload?.data) {
          state.ratings = [action.payload.data, ...state.ratings];
        }
        state.submitError = null;
      })
      .addCase(submitNewRating.rejected, (state, action) => {
        state.submitStatus = 'failed';
        state.submitError = action.payload || 'Failed to submit rating';
      })
      
      // Fetch ratings
      .addCase(fetchRatings.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchRatings.fulfilled, (state, action) => {
        state.loading = false;
        if (action.payload?.data) {
          state.ratings = action.payload.data;
        }
        if (action.payload?.pagination) {
          state.pagination = action.payload.pagination;
        }
        state.error = null;
      })
      .addCase(fetchRatings.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || 'Failed to fetch ratings';
      })
      
      // Fetch rating by id
      .addCase(fetchRatingById.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchRatingById.fulfilled, (state, action) => {
        state.loading = false;
        state.currentRating = action.payload?.data || null;
        state.error = null;
      })
      .addCase(fetchRatingById.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || 'Failed to fetch rating';
      });
  }
});

// Export actions
export const { 
  clearRatingError, 
  resetSubmitStatus, 
  clearCurrentRating 
} = ratingSlice.actions;

// Selectors
export const selectAllRatings = (state) => state.rating.ratings;
export const selectCurrentRating = (state) => state.rating.currentRating;
export const selectRatingPagination = (state) => state.rating.pagination;
export const selectRatingLoading = (state) => state.rating.loading;
export const selectRatingError = (state) => state.rating.error;
export const selectSubmitStatus = (state) => state.rating.submitStatus;
export const selectSubmitError = (state) => state.rating.submitError;

// Export reducer
export default ratingSlice.reducer;